import { ViewModel } from 'view_models/view_model'
import { Sample } from 'entities/sample'
import { Badge, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { Flex } from 'components/shared/flex'
import moment from 'moment'
import { ReactNode } from 'react'
import { Result } from 'entities/result'

export class SampleViewModel extends ViewModel<Sample> {
    public collectionDateText = (): string => {
        return this.toDateText(this.object.collectionDate)
    }
    public deliveryDateText = (): string => {
        return this.toDateText(this.object.deliveryDate)
    }
    public inspectionDateText = (): string => {
        return this.toDateText(this.object.inspectionDate)
    }
    public sendDateText = (): string => {
        return this.toDateText(this.object.sendDate)
    }

    public agreeStudy = (): ReactNode => {
        return (
            this.object.followupStudyAgreement !== undefined && (
                <Badge bg={this.object.followupStudyAgreement ? 'success' : 'danger'}>{this.object.followupStudyAgreement ? '同意' : '拒否'}</Badge>
            )
        )
    }
    public testBadge = (): ReactNode => {
        return <Badge bg={this.object.isTest ? 'danger' : 'primary'}>{this.object.isTest ? 'テスト用' : '本番用'}</Badge>
    }

    public deadline = (): JSX.Element | undefined => {
        if (this.object.status === 'SENT') {
            return
        }
        if (this.object.status === 'INTERVIEWED' || this.object.status === 'RECOLLECT') {
            return
        }
        if (!this.object.institution?.id) {
            return
        }
        const start = moment()
        const end = moment(this.object.deadlineDate)
        const diff = end.diff(start, 'days')
        let color = 'secondary'
        if (diff < 2) {
            color = 'danger'
        } else if (diff < 5) {
            color = 'warning'
        }
        if (diff < 0) {
            return <Badge bg={color}>{Math.abs(diff)}日超過</Badge>
        }

        return <Badge bg={color}>残り{diff}日</Badge>
    }

    public statusView = (): JSX.Element => {
        let text
        let bg
        let color
        switch (this.object.status) {
            case 'WAREHOUSED':
                text = '検査待ち'
                bg = 'danger'
                break
            case 'RNA_EXTRACTED':
                text = '抽出・精製済'
                bg = 'warning'
                break
            case 'DNA_SYNTHESIZED':
                text = 'cDNA合成済'
                bg = 'info'
                break
            case 'PCR_READY':
                text = 'qPCR準備中'
                color = '#9575CD'
                break
            case 'COMPLETED':
                text = '検査済み'
                bg = 'primary'
                break
            case 'PRINT_REQUESTED':
                text = '印刷依頼済'
                color = '#E64A19'
                break
            case 'SENT':
                text = '送付済'
                bg = 'success'
                break
            case 'INTERVIEWED':
                text = '問診票入力済'
                color = '#721329'
                break
            case 'DISPOSED':
                text = '廃棄済'
                color = '#d4d4d4'
                break
            case 'NGS_IN_PROGRESS':
                text = 'NGS測定中'
                color = '#9575CD'
                break
            case 'RECOLLECT':
                text = '再採尿'
                color = '#03DAC5'
                break
            default:
                break
        }

        const isRerun = (this.object.extractionCount || 0) > 0 && this.object.status === 'WAREHOUSED'
        return (
            <div style={{ position: 'relative' }}>
                {isRerun && (
                    <Flex
                        alignItems="center"
                        justifyContent="center"
                        style={{
                            position: 'absolute',
                            top: -10,
                            left: 0,
                            backgroundColor: '#ffc107',
                            width: 25,
                            height: 25,
                            textAlign: 'center',
                            borderRadius: 20,
                        }}
                    >
                        再
                    </Flex>
                )}

                <Badge bg={bg || ''} style={{ width: 80, backgroundColor: color }}>
                    {text}
                </Badge>
            </div>
        )
    }
    public inspectionStatusView = (): JSX.Element => {
        let text
        let bg
        let color
        switch (this.object.inspectionStatus) {
            case 'DRAFT':
                text = '入力待ち'
                bg = 'secondary'
                break
            case 'INPUTTED':
                text = '入力済'
                bg = 'info'
                break
            case 'INSPECTED':
                text = '確認済み'
                bg = 'success'
                break
            default:
                break
        }
        return (
            <div style={{ position: 'relative' }}>
                <Badge bg={bg || ''} style={{ width: 80, backgroundColor: color }}>
                    {text}
                </Badge>
            </div>
        )
    }

    public getReferenceReason = (result?: Result): string[] => {
        const reasons = []
        if (this.object.overTemperature) {
            reasons.push('温度逸脱')
        }
        if (this.object.menstruation || this.object.background?.menstruation) {
            reasons.push('生理中')
        }
        if (this.object.pregnancy || this.object.background?.pregnancy) {
            reasons.push('妊娠中')
        }
        if (this.object.includeBlood) {
            reasons.push('血液混入')
        }
        if (this.object.quantityStatus === 'REFERENCE') {
            reasons.push('尿量不足')
        }
        if (result && result.isReferenceByBiomarker) {
            reasons.push('基準値逸脱')
        }
        return reasons
    }

    public resultStatusView = (): JSX.Element => {
        if (this.object.resultStatus === 'OK') {
            return <div style={{ width: 60, fontSize: 10, textAlign: 'center', color: '#00797B', border: '1px solid #00797B', borderRadius: 3 }}>正常</div>
        } else if (this.object.resultStatus === 'REFERENCE') {
            return (
                <OverlayTrigger
                    placement="right"
                    overlay={
                        <Tooltip id={`result-status-${this.object.id}`}>
                            {this.object.overTemperature && <div>温度逸脱({this.object.temperature}℃)</div>}
                            {(this.object.menstruation || this.object.background?.menstruation) && <div>生理中</div>}
                            {(this.object.pregnancy || this.object.background?.pregnancy) && <div>妊娠中</div>}
                            {this.object.includeBlood && <div>血液混入</div>}
                            {this.object.quantityStatus === 'REFERENCE' && <div>尿量不足</div>}
                        </Tooltip>
                    }
                >
                    <div style={{ width: 60, fontSize: 10, textAlign: 'center', color: '#ffc107', border: '1px solid #ffc107', borderRadius: 3 }}>参考値</div>
                </OverlayTrigger>
            )
        } else {
            return (
                <OverlayTrigger
                    placement="right"
                    overlay={
                        <Tooltip id={`result-status-${this.object.id}`}>
                            {this.object.differentContainer && <div>指定容器以外</div>}
                            {this.object.nonUrine && <div>尿以外の検体</div>}
                            {this.object.quantityStatus === 'NG' && <div>尿量が1ml以下の検体</div>}
                        </Tooltip>
                    }
                >
                    <div style={{ width: 60, fontSize: 10, textAlign: 'center', color: '#dc3545', border: '1px solid #dc3545', borderRadius: 3 }}>検査不可</div>
                </OverlayTrigger>
            )
        }
    }

    public productItemView = (overlay?: boolean): JSX.Element => {
        const completeProductItemIds = this.object.results?.map((r) => r.productItem?.id).filter((i) => !!i)
        return (
            <Flex alignItems="center" className="child-margin-r-5">
                <Flex style={{}} flexWrap="wrap" alignItems="center">
                    {this.object.productItems?.map((pi, index) => {
                        return (
                            <Flex key={'color-' + index} alignItems="center" className="" style={{ position: 'relative' }}>
                                <div style={{ marginBottom: 1, height: 8, width: 8, borderRadius: 10, backgroundColor: pi.color, marginRight: 5 }}></div>
                                <div>{pi.name}</div>
                                {overlay && completeProductItemIds?.includes(pi.id) && (
                                    <div style={{ backgroundColor: '#8885', width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}></div>
                                )}
                            </Flex>
                        )
                    })}
                </Flex>
            </Flex>
        )
    }

    public getSampleResultStatusBadge = (): JSX.Element => {
        let badgeColor = ''
        let badgeText = ''

        if (this.object.status === 'SENT' && !!this.object.sendDate) {
            badgeColor = 'success'
            badgeText = '発送済み'
        } else if (this.object.isPrintResultChecked && this.object.isResultChecked) {
            badgeColor = 'warning'
            badgeText = '発送待ち'
        } else if (this.object.isPrintResultChecked) {
            badgeColor = 'info'
            badgeText = '印刷済み'
        } else if (this.object.isResultChecked) {
            badgeColor = 'primary'
            badgeText = '内容確認済み'
        } else {
            badgeColor = 'secondary'
            badgeText = '未確認'
        }

        return (
            <Badge bg={badgeColor} style={{ width: '80px', textAlign: 'center' }}>
                {badgeText}
            </Badge>
        )
    }
}
